import moment from 'moment'
import find from 'lodash/find'
import get from 'lodash/get'
import orderBy from 'lodash/orderBy'

import { pad, capitalize } from 'utilities/text'
import leftPad from 'left-pad'

export function getFlatDescription(address) {
    if (!address.flatNo) {
        return ''
    }

    return address.floorType + pad(address.floorNo, 2, '0') + pad(address.flatNo, 2, '0')
}

export function formatAddress(address) {
    return [capitalize(address.streetName), formatStreetNo(address), getFlatDescription(address)]
        .join(' ')
        .trim()
}

export function setPrimaryAddresses(groupedAddresses, allAddresses) {
    const primary = allAddresses.filter((address) => address.isPrimary)

    primary.forEach((a) => {
        const address =
            find(groupedAddresses, { addressId: a.addressId }) ||
            find(groupedAddresses, {
                deliveryAddress: { deliveryPointId: a.deliveryAddress.deliveryPointId },
            })

        if (address) {
            address.isPrimary = true
            address.validFrom = a.validFrom
            address.validBefore = a.validBefore
        }
    })
}

export function isValidNow(address) {
    const now = moment().startOf('day')

    if (address.validFrom && moment(address.validFrom, 'YYYY-MM-DD').isAfter(now)) {
        return false
    }

    if (address.validBefore && !now.isBefore(moment(address.validBefore, 'YYYY-MM-DD'))) {
        return false
    }

    return true
}

export function findCurrentPrimaryAddress(addresses) {
    return addresses.filter((a) => a.isPrimary && isValidNow(a)).map((a) => a.deliveryAddress)[0]
}

export function findNextPrimaryAddress(addresses) {
    addresses = addresses.filter((a) => a.isPrimary)
    addresses = orderBy(addresses, 'validFrom', 'desc')

    return addresses.map((a) => a.deliveryAddress)[0]
}

export function findSameAddress(addresses, address = {}) {
    const { deliveryPointId, firstName, lastName } = address

    return find(addresses, {
        deliveryPointId,
        firstName,
        lastName,
    })
}

export function makeDeliveryAddress(address, personalInfo, transportProduct) {
    const addr = {
        ...address,
        ...personalInfo,
    }

    return {
        firstName: addr.firstName,
        lastName: addr.lastName,
        mobile: addr.mobile,
        deliveryPointId: addr.deliveryPointId,
        streetName: addr.streetName,
        streetNo: addr.streetNo,
        entrance: addr.entrance,
        floorNo: addr.floorNo,
        floorType: addr.floorType,
        flatNo: addr.flatNo,
        co: addr.co,
        zip: addr.zip,
        city: addr.city,
        countryCode: addr.countryCode,
        deliverySms: addr.deliverySms,
        transportProduct,
    }
}

function findBestMatch(items, value, itemFormatter) {
    if (!Array.isArray(items) || items.length === 0) return -1
    if (typeof value !== 'string') return -1

    const trimmedValue = value.toLowerCase().replace(/\s/g, '')

    if (trimmedValue.length === 0) return -1

    const trimmedItemValues = items.map((item) =>
        itemFormatter(item).toLowerCase().replace(/\s/g, '')
    )

    const indexStartsWith = trimmedItemValues.findIndex((itemValue) =>
        itemValue.startsWith(trimmedValue)
    )
    if (indexStartsWith >= 0) return indexStartsWith

    const indexContains = trimmedItemValues.findIndex(
        (itemValue) => itemValue.indexOf(trimmedValue) >= 0
    )
    if (indexContains >= 0) return indexContains

    return -1
}

export function formatStreetName(street) {
    return `${capitalize(street.streetName)}, ${street.city}`
}

export function formatStreetNo(streetNumber) {
    return `${streetNumber.streetNo}${streetNumber.entrance ? streetNumber.entrance : ''}`
}

export function formatFloor(t, floor) {
    return t('addressHelperFloorType' + floor.floorType, {
        floorNo: floor.floorNo,
    })
}

export function formatHousehold(t, floorType, floorNo, flatNo) {
    return t('addressHelperFlat' + floorType, {
        floorNo: leftPad(floorNo, 2, '0'),
        flatNo: leftPad(flatNo, 2, '0'),
    })
}

export function findBestStreetNameMatch(items, value) {
    return findBestMatch(items, value, formatStreetName)
}

export function findBestStreetNoMatch(items, value) {
    return findBestMatch(items, value, formatStreetNo)
}

export function findBestFloorMatch(t, items, value) {
    return findBestMatch(items, value, (item) => formatFloor(t, item))
}

export function findBestHouseholdMatch(t, items, floorType, floorNo, value) {
    return findBestMatch(items, value, (item) =>
        formatHousehold(t, floorType, floorNo, item.flatNo)
    )
}

export function getDeliveryPointFromState(state) {
    return get(state, 'address.active.deliveryPointId', null)
}

export function getZipCodeFromState(state) {
    return get(state, 'address.active.zip', null)
}

export function getFormattedStreetAddress(address, nonBreakingWhitespace) {
    if (!address || !address.streetName) {
        return ''
    }

    return `${capitalize(address.streetName)}${nonBreakingWhitespace ? '&nbsp;' : ' '}${
        address.streetNo || ''
    }${address.entrance || ''}`
}
